import React, { useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';

/**
 * TEMPORARY REPAIR TOOL
 * 
 * Purpose: Reconstruct the missing t-shirt order "wr4TDTm3JRVQNsjTybad"
 * for Natasha Oosthuysen (28 students, dated 6 Feb 2026).
 * 
 * The student documents were updated correctly but the order document
 * in tshirtOrders was lost. This tool rebuilds it.
 * 
 * HOW TO USE:
 * 1. Add this component to your AdminDashboard.js (instructions below)
 * 2. Click "Step 1: Find Students" to see the 28 students
 * 3. Review the list
 * 4. Click "Step 2: Recreate Order" to rebuild the missing order document
 * 5. Once confirmed working, remove this component from AdminDashboard
 * 
 * TO ADD TO AdminDashboard.js:
 *   - Add import: import RepairTshirtOrder from './RepairTshirtOrder';
 *   - Add a button in the dashboard that shows it, e.g.:
 *     {currentView === 'repairOrder' && <RepairTshirtOrder onClose={() => setCurrentView('dashboard')} />}
 *   - Add a button to open it, e.g.:
 *     <button onClick={() => setCurrentView('repairOrder')}>🔧 Repair Missing Order</button>
 */

function RepairTshirtOrder({ onClose }) {
  const [step, setStep] = useState(0); // 0 = initial, 1 = students found, 2 = order created
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [franchiseeName, setFranchiseeName] = useState('');
  const [orderExists, setOrderExists] = useState(false);

  const MISSING_ORDER_ID = 'wr4TDTm3JRVQNsjTybad';
  const ORDER_DATE = new Date('2026-02-06T00:00:00Z'); // 6 February 2026

  // STEP 1: Find all students linked to the missing order
  const findStudents = async () => {
    setLoading(true);
    setError('');
    try {
      // First check if the order already exists (in case you run this twice)
      const existingOrder = await getDoc(doc(db, 'tshirtOrders', MISSING_ORDER_ID));
      if (existingOrder.exists()) {
        setOrderExists(true);
        setError('⚠️ Order document already exists! No repair needed.');
        setLoading(false);
        return;
      }

      // Find all students with this order ID
      const q = query(
        collection(db, 'students'),
        where('tshirtOrderId', '==', MISSING_ORDER_ID)
      );

      const snapshot = await getDocs(q);
      const foundStudents = [];

      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        foundStudents.push({
          id: docSnap.id,
          studentName: data.studentName || 'Unknown',
          schoolName: data.schoolName || 'Unknown',
          tShirtSize: data.tShirtSize || 'Unknown',
          adminFee: data.adminFee || 160,
          franchiseeRef: data.franchiseeRef
        });
      });

      // Sort by school then student name
      foundStudents.sort((a, b) => {
        if (a.schoolName !== b.schoolName) {
          return a.schoolName.localeCompare(b.schoolName);
        }
        return a.studentName.localeCompare(b.studentName);
      });

      setStudents(foundStudents);

      // Get franchisee name from the first student's reference
      if (foundStudents.length > 0 && foundStudents[0].franchiseeRef) {
        try {
          const franchiseeDoc = await getDoc(foundStudents[0].franchiseeRef);
          if (franchiseeDoc.exists()) {
            setFranchiseeName(franchiseeDoc.data().name || 'Natasha Oosthuysen');
          }
        } catch (e) {
          setFranchiseeName('Natasha Oosthuysen');
        }
      }

      setStep(1);
    } catch (err) {
      console.error('Error finding students:', err);
      setError('Error finding students: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // STEP 2: Recreate the order document
  const recreateOrder = async () => {
    if (!window.confirm(
      `Recreate missing order with ${students.length} students?\n\n` +
      `Order ID: ${MISSING_ORDER_ID}\n` +
      `Date: 6 February 2026\n` +
      `Franchisee: ${franchiseeName}\n` +
      `Students: ${students.length}\n` +
      `Cost: R${(students.length * 120).toFixed(2)}\n\n` +
      `Proceed?`
    )) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build the studentTshirts array (same format as TShirtOrderForm uses)
      const studentTshirts = students.map(student => ({
        studentRef: doc(db, 'students', student.id),
        studentName: student.studentName,
        schoolName: student.schoolName,
        size: student.tShirtSize,
        adminFeeAmount: student.adminFee,
        cost: 0
      }));

      const studentCount = students.length;
      const studentTshirtCost = studentCount * 120; // R120 per student

      // Load admin fee structure for accurate pricing
      let tshirtCostToHO = 110; // default
      let adultPricingSL = 200;
      let adultPricingXL = 200;
      let stickerPrice = 70;

      try {
        const adminFeeDoc = await getDoc(doc(db, 'adminFeeStructure', 'current'));
        if (adminFeeDoc.exists()) {
          const feeData = adminFeeDoc.data();
          tshirtCostToHO = feeData.tshirtCostToHO || 110;
          adultPricingSL = feeData.adultTshirtPricing?.smallToLarge || 200;
          adultPricingXL = feeData.adultTshirtPricing?.xlToXxxxl || 200;
          stickerPrice = feeData.stickerPricePerRoll || 70;
        }
      } catch (e) {
        console.warn('Could not load admin fee structure, using defaults');
      }

      // Build the order document (matching exact structure from TShirtOrderForm.handleSubmitOrder)
      const orderData = {
        orderNumber: `TS-2026-REPAIR`,
        orderDate: Timestamp.fromDate(ORDER_DATE),
        franchiseeRef: students[0].franchiseeRef, // Same franchisee for all students
        franchiseeName: franchiseeName,
        studentTshirts: studentTshirts,
        adultTshirts: {
          smallToLarge: {
            quantity: 0,
            sizes: { S: 0, M: 0, L: 0 },
            priceEach: adultPricingSL,
            total: 0
          },
          xlToXxxxl: {
            quantity: 0,
            sizes: { XL: 0, XXL: 0, XXXL: 0 },
            priceEach: adultPricingXL,
            total: 0
          }
        },
        stickers: {
          quantity: 0,
          pricePerRoll: stickerPrice,
          total: 0
        },
        extraStock: {
          '2-3 YEARS': 0,
          '4-5 YEARS': 0,
          '5-6 YEARS': 0,
          '7-8 YEARS': 0,
          '9-10 YEARS': 0,
          '11-12 YEARS': 0,
          '13-14 YEARS': 0,
          'S': 0,
          'M': 0,
          'L': 0
        },
        extraStockCount: 0,
        extraStockCost: 0,
        studentTshirtCount: studentCount,
        adultTshirtTotal: 0,
        stickerTotal: 0,
        totalCost: studentTshirtCost,
        tshirtCostToHO: tshirtCostToHO,
        totalTshirts: studentCount,
        totalCostToHO: studentCount * tshirtCostToHO,
        status: 'pending',
        shipped: false,
        shippedDate: null,
        deliveredDate: null,
        notes: 'REPAIRED: Order document was lost. Reconstructed from student records on ' + new Date().toLocaleDateString('en-ZA'),
        includeInMonthEnd: true,
        createdAt: Timestamp.fromDate(ORDER_DATE),
        updatedAt: Timestamp.fromDate(new Date())
      };

      // Use setDoc with the EXACT same document ID so students still link to it
      await setDoc(doc(db, 'tshirtOrders', MISSING_ORDER_ID), orderData);

      console.log('✅ Order recreated successfully!');
      setStep(2);
    } catch (err) {
      console.error('Error recreating order:', err);
      setError('Error recreating order: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get size summary for display
  const getSizeSummary = () => {
    const summary = {};
    students.forEach(s => {
      summary[s.tShirtSize] = (summary[s.tShirtSize] || 0) + 1;
    });
    return summary;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '30px',
        maxWidth: '800px',
        width: '95%',
        maxHeight: '85vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ margin: 0, fontSize: '22px' }}>🔧 Repair Missing T-Shirt Order</h1>
          <button onClick={onClose} style={{
            background: '#ff4444',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '16px'
          }}>✕</button>
        </div>

        <div style={{
          background: '#fff3cd',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#856404',
          border: '1px solid #ffc107'
        }}>
          <strong>What happened:</strong> Natasha placed a t-shirt order on 6 Feb for 28 students. 
          The students were updated correctly, but the order document in <code>tshirtOrders</code> was lost. 
          This tool rebuilds it.
        </div>

        {error && (
          <div style={{
            background: '#f8d7da',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            color: '#721c24',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        {/* STEP 0: Initial */}
        {step === 0 && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ fontSize: '16px', color: '#555', marginBottom: '20px' }}>
              Missing Order ID: <code style={{ background: '#f0f0f0', padding: '4px 8px', borderRadius: '4px' }}>{MISSING_ORDER_ID}</code>
            </p>
            <button
              onClick={findStudents}
              disabled={loading}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? '🔍 Searching...' : '🔍 Step 1: Find the 28 Students'}
            </button>
          </div>
        )}

        {/* STEP 1: Students Found - Review */}
        {step === 1 && (
          <>
            <div style={{
              background: '#d4edda',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              color: '#155724',
              border: '1px solid #c3e6cb'
            }}>
              ✅ Found <strong>{students.length} students</strong> linked to the missing order
            </div>

            {/* Size Summary */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '16px'
            }}>
              {Object.entries(getSizeSummary()).sort().map(([size, count]) => (
                <span key={size} style={{
                  background: '#e7f3ff',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#1976d2'
                }}>
                  {size}: {count}
                </span>
              ))}
            </div>

            {/* Student List */}
            <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>#</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Student</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>School</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Size</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, idx) => (
                    <tr key={student.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px 10px', color: '#888' }}>{idx + 1}</td>
                      <td style={{ padding: '8px 10px' }}>{student.studentName}</td>
                      <td style={{ padding: '8px 10px', color: '#666' }}>{student.schoolName}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 'bold', color: '#4CAF50' }}>{student.tShirtSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cost Summary */}
            <div style={{
              background: '#f0f0ff',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Student T-shirts ({students.length} × R120):</span>
                <strong>R{(students.length * 120).toFixed(2)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Adult T-shirts:</span>
                <strong>R0.00</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Stickers:</span>
                <strong>R0.00</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #667eea', paddingTop: '8px', marginTop: '8px' }}>
                <strong style={{ fontSize: '16px' }}>Total Due to HO:</strong>
                <strong style={{ fontSize: '16px', color: '#667eea' }}>R{(students.length * 120).toFixed(2)}</strong>
              </div>
            </div>

            <button
              onClick={recreateOrder}
              disabled={loading}
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                width: '100%'
              }}
            >
              {loading ? '⏳ Recreating Order...' : `✅ Step 2: Recreate Order (${students.length} students - R${(students.length * 120).toFixed(2)})`}
            </button>
          </>
        )}

        {/* STEP 2: Success */}
        {step === 2 && (
          <div style={{ textAlign: 'center', padding: '30px 20px' }}>
            <div style={{ fontSize: '60px', marginBottom: '16px' }}>🎉</div>
            <h2 style={{ color: '#28a745', marginBottom: '12px' }}>Order Restored Successfully!</h2>
            <p style={{ color: '#555', fontSize: '16px', marginBottom: '8px' }}>
              Order <code>{MISSING_ORDER_ID}</code> has been recreated with {students.length} students.
            </p>
            <p style={{ color: '#555', fontSize: '14px', marginBottom: '24px' }}>
              Natasha should now see this order on the admin side. Check the T-Shirt Orders page to verify.
            </p>
            <div style={{
              background: '#fff3cd',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: '14px',
              color: '#856404'
            }}>
              <strong>Remember:</strong> Remove this repair tool from AdminDashboard.js once you've confirmed everything is working.
            </div>
            <button
              onClick={onClose}
              style={{
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Done - Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RepairTshirtOrder;